/** @format */
import { Component, ElementRef, Inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UtilityService, WINDOW } from '@shared/utils/services';
import {
	BopisItemsByStore,
	BopisProxyPerson,
	IdentityV2,
	SameDayGroup,
	SortedBopisItemGroup,
	ShippingGroup,
	ShippingItemsByEdd,
	OrderItemReturnedOrCanceledList
} from '@my-account/data-access/interfaces';
import { HmfModalComponent } from '@dsg-tech/homefield-angular';

import { AdjustmentDialogComponent } from './adjustment-dialog/adjustment-dialog.component';
import { ManualAdjustmentDialogComponent } from './manual-adjustment-dialog/manual-adjustment-dialog.component';
import { TaxDialogComponent } from './tax-dialog/tax-dialog.component';

import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { first } from 'rxjs/operators';
import { ConfirmationEmailModalComponent } from './confirmation-email-modal/confirmation-email-modal.component';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Adjustment, Chain, KillSwitches, Order, OrderItem, PersonalInformation, StoreInfo } from '@shared/data-access/interfaces';
import { MyAccountAnalyticsService, Store } from '@shared/data-access/services';
import { BazaarvoiceFormService } from '@product-reviews/utils';

@Component({
	selector: 'my-account-order-track-components-order-detail-cards',
	templateUrl: './order-detail-cards.component.html',
	styleUrls: ['./order-detail-cards.component.scss']
})
export class OrderDetailCardsComponent implements OnInit, OnChanges {
	@ViewChild('checkinComponent') checkinComponent: ElementRef;
	@ViewChild(ConfirmationEmailModalComponent) confirmationEmail: ConfirmationEmailModalComponent | undefined;



	@Input()
		trackOrder = false;

	@Input()
		orderDetail: Order;

	@Input()
		resetCheckIn: BehaviorSubject<boolean>;

	@Input()
		zipCode?: string;

	// initialize the buckets
	bopisItemsBucket: OrderItem[] = [];
	sthItemsBucket: OrderItem[] = [];
	sameDayItemsBucket: OrderItem[] = [];
	otherItemsBucket: OrderItem[] = [];
	preSortedShipmentsByMethod: OrderItem[] = [];
	tempSortedShippingOrder: OrderItem[] = []; // sorted by edd
	tempSortedBopisItems: OrderItem[] = [];
	sortedBopisItems: BopisItemsByStore[] = [];
	sortedSameDayItems: OrderItem[] = [];
	sortedActiveBopisItems: SortedBopisItemGroup[] = [];
	sortedCancelledBopisItems: SortedBopisItemGroup[] = [];
	sortedReturnedBopisItems: SortedBopisItemGroup[] = [];
	sortedShippingItems: ShippingItemsByEdd[] = [];
	orderProxyPerson: BopisProxyPerson;
	sortedShippingGroups: ShippingGroup[] = [];
	sortedSameDayGroups: SameDayGroup[] = [];
	returnedOrCancelledShippingItems: OrderItemReturnedOrCanceledList = {
		cancelled: [],
		returned: [],
	};
	returnedOrCancelledSameDayDeliveryItems: OrderItemReturnedOrCanceledList = {
		cancelled: [],
		returned: [],
	};
	orderCompleted = false; // used for the 'start a return' button
	orderAdjustments: Adjustment[];
	orderManualAdjustments: Adjustment[];
	noTrackItems: OrderItem[] = [];
	trackingItems: OrderItem[] = [];
	sddNoTracking: OrderItem[] = [];
	sddHasTracking: OrderItem[] = [];
	proxyPickup;
	buyAgainEnabled = false;
	reviewsSubmissionFormV2Enabled = false;
	inStoreOrder = false;
	identity: IdentityV2;
	isTrackOrderPage = this.window.location.pathname.includes('TrackOrder');

	@ViewChild(HmfModalComponent) resendConfirmationModal: HmfModalComponent | undefined;

	constructor(
		private analyticsService: MyAccountAnalyticsService,
		private utilityService: UtilityService,
		private chain: Chain,
		private store: Store,
		private dialog: MatDialog,
		@Inject(WINDOW)
		protected window: Window,
		private bvFormService: BazaarvoiceFormService
	) {}

	ngOnInit(): void {
		this.getKillswitches();

		this.setZipCode(this.orderDetail.zipCode);
		this.setOrderAdjustments(this.orderDetail.adjustments);
		this.setManualAdjustments(this.orderDetail.manualAdjustments);
		this.setPdpUrls(this.orderDetail.items);
		this.sortOrderByFulfilmentMethod(this.orderDetail.items);
		this.tempSortedBopisItems = this.sortBopisItemsByPickupLocation(this.bopisItemsBucket);
		this.sortBopisItems(this.bopisItemsBucket);
		this.orderProxyPerson = this.getBopisProxyIfPresent(this.bopisItemsBucket);
		this.sortedShippingGroups = this.sortShippingItems(this.sthItemsBucket);
		this.sortedSameDayGroups = this.sortSameDayItems(this.sameDayItemsBucket);
		this.findProxyInfo(this.orderDetail.items);
		this.getContactInfoForInStoreOrder(this.orderDetail.storeInfo);
	}

	ngOnChanges(changes: SimpleChanges): void {
		// only call this on load of a different order.
		if (changes.orderDetail?.previousValue && changes.orderDetail.currentValue.orderNumber !== changes.orderDetail.previousValue.orderNumber) {
			const currentOrderDetail = changes.orderDetail.currentValue;
			// reset buckets
			this.resetOrderTrack();
			// get new values for ZIP, payments, and adjustments
			this.setZipCode(currentOrderDetail?.zipCode);
			this.setOrderAdjustments(currentOrderDetail?.adjustments);
			this.setManualAdjustments(currentOrderDetail?.manualAdjustments);
			// re-sort items
			this.sortOrderByFulfilmentMethod(currentOrderDetail?.items);
			this.tempSortedBopisItems = this.sortBopisItemsByPickupLocation(this.bopisItemsBucket);
			this.sortBopisItems(this.bopisItemsBucket);
			this.orderProxyPerson = this.getBopisProxyIfPresent(this.bopisItemsBucket);
			this.sortedShippingGroups = this.sortShippingItems(this.sthItemsBucket);
			this.sortedSameDayGroups = this.sortSameDayItems(this.sameDayItemsBucket);
		}
	}

	/**
	 * breaks items into three preliminary buckets: bopis items, active shipping, cancelled shipping
	 * @param items the items input from the order detail template.
	 */
	sortOrderByFulfilmentMethod(items: OrderItem[]): void {
		for (const item of items) {
			if (item.bopisItem) {
				this.bopisItemsBucket.push(item);
			}
			// shipping items (NOT SAMEDAY)
			if (!item.bopisItem && item.status !== 'Cancelled' && item.status !== 'Returned' && !item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.sthItemsBucket.push(item);
			}
			if (!item.bopisItem && (item.status === 'Cancelled' || item.status === 'Returned') && !item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.returnedOrCancelledShippingItems[item.status.toLocaleLowerCase()].push(item);
			}
			// Same Day Delivery items
			if (item.shipModeCode.toUpperCase().includes('SAMEDAY') && !(item.status === 'Cancelled' || item.status === 'Returned') ) {
				this.sameDayItemsBucket.push(item);
			}
			if (!item.bopisItem && (item.status === 'Cancelled' || item.status === 'Returned') && item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.returnedOrCancelledSameDayDeliveryItems[item.status.toLocaleLowerCase()].push(item);
			}
		}
	}

	get CancelledOrReturnedShippingItems() {
		return Object.keys(this.returnedOrCancelledShippingItems);
	}

	get CancelledOrReturnedSameDayDeliveryItems() {
		return Object.keys(this.returnedOrCancelledSameDayDeliveryItems);
	}
	sortSameDayItems(items) {
		const sameDayGroups: SameDayGroup[] = [];
		const trackingNumbers = this.reduceValues(this.sameDayItemsBucket, 'trackingNumber');

		// break up by tracking / no tracking
		items.forEach((item) => {
			if (item.trackingNumber === '') {
				this.sddNoTracking.push(item);
			} else {
				this.sddHasTracking.push(item);
			}
		});

		// create a SameDayGroup per unique tracking number
		trackingNumbers.forEach((trackingNum) => {
			sameDayGroups.push({
				groupTrackingNumber: trackingNum !== 'NA' ? trackingNum : 'NO_TRACKING_AVAILABLE_YET',
				items: []
			});
		});
		// create one group of items without a tracking number yet - they will be sorted into a group when a tracking number is available
		if (this.sddNoTracking.length > 0) {
			sameDayGroups.push({
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: []
			});
		}
		// sort items with a tracking number into the correct SameDayGroup
		for (const item of this.sddHasTracking) {
			// get the placeholder image if one has not been returned for the item
			if (item.imageIdentifier === '') {
				item.imageIdentifier = 'IMAGE_NOT_FOUND';
			}
			for (const sddGroup of sameDayGroups) {
				if (item.trackingNumber === sddGroup.groupTrackingNumber) {
					sddGroup.groupDistributionOrder = item?.orderTracking?.distributionOrder;
					sddGroup.items.push(item);
					sddGroup.groupStatusCode = item.status;
					sddGroup.groupTrackingUrl = item.trackingUrl;
					sddGroup.groupHeaderText = this.getGroupHeaderText(item.status);
					sddGroup.groupEstimatedDelivery = item.estimatedDelivery;
					if (item.estimatedDelivery !== null) {
						sddGroup.groupEstimatedDelivery = item.estimatedDelivery;
					} else {
						sddGroup.groupEstimatedDelivery = '';
					}
					sddGroup.groupShippingAddress = item.shippingRecipient?.address?.address1 || '';
					sddGroup.groupShippingCity = item.shippingRecipient?.address?.city || '';
					sddGroup.groupShippingState = item.shippingRecipient?.address?.state || '';
				}
			}


		}
		// sort the remaining same day items
		for (const item of this.sddNoTracking) {
			if (item.imageIdentifier === '') {
				item.imageIdentifier = 'IMAGE_NOT_FOUND';
			}
			const sddGroupsNoTrack: SameDayGroup[] = [];
			sameDayGroups.forEach((group) => {
				if (group.groupTrackingNumber === 'NO_TRACKING_AVAILABLE_YET') {
					sddGroupsNoTrack.push(group);
				}
			});

			for (const sddGroup of sddGroupsNoTrack) {
				sddGroup.items.push(item);
				sddGroup.groupStatusCode = item.status;
				sddGroup.groupHeaderText = this.getGroupHeaderText(item.status);
				sddGroup.groupEstimatedDelivery = item.estimatedDelivery;
			}
		}
		return sameDayGroups;
	}

	/**
	 * matches the appropriate ui display group header text with the tracking status
	 * @param trackingStatus
	 * @returns group header text
	 */
	getGroupHeaderText(status: string): string {
		switch (status) {
			case 'Preparing Delivery': {
				return 'Same Day Delivery';
			}
			case 'On the Way': {
				return 'On the Way with DoorDash';
			}
			case 'Delivered': {
				return 'Delivered by DoorDash';
			}
			case 'Undeliverable': {
				return 'Undeliverable';
			}
			case 'Preparing for Shipment': {
				return 'Preparing';
			}
			default: {
				return '';
			}
		}
	}

	/**
	 * Sorts shipping items into shippingGroups based on tracking number AND method of delivery (ground parcel/2nd Day parcel/next day parcel)
	 * @param items All items from the sthItemsBucket (items that are shipping)
	 * @returns shippingGroups sorted by EDD
	 */
	sortShippingItems(items) {
		const shippingGroups: ShippingGroup[] = [];
		const trackingNumbers = this.reduceValues(this.sthItemsBucket, 'trackingNumber');

		// counters for shipping items without a tracking number yet
		let ground = 0;
		let secondDay = 0;
		let nextDay = 0;
		let ltl = 0;

		// break up shipment items even further - an array of items with tracking and an arrray without
		items.forEach((item) => {
			if (item.trackingNumber === '') {
				this.noTrackItems.push(item);
			} else {
				this.trackingItems.push(item);
			}
		});

		trackingNumbers.forEach((trackingNum) => {
			shippingGroups.push({
				groupShipModeCode: undefined,
				groupTrackingNumber: trackingNum === 'NA' ? 'NO_TRACKING_AVAILABLE_YET' : trackingNum,
				items: [],
			});
		});


		// determine how many different shipment methods we've got for items w/o a tracking number
		this.noTrackItems.forEach((item) => {
			switch (item.shipModeCode.substring(0,3).toLowerCase()) {
				// ground parcel
				case 'gro': {
					ground++;
					break;
				}
				// 2nd day parcel
				case '2nd': {
					secondDay++;
					break;
				}
				// next day parcel
				case 'nex': {
					nextDay++;
					break;
				}
				// LTL
				case 'ltl': {
					ltl++;
					break;
				}
			}
		});

		// and create ShippingGroups for any of our shipmodes w/o tracking
		if (ground > 0) {
			shippingGroups.push({
				groupShipModeCode: 'Ground Parcel',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}
		if (secondDay > 0) {
			shippingGroups.push({
				groupShipModeCode: '2nd Day Parcel',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}
		if (nextDay > 0) {
			shippingGroups.push({
				groupShipModeCode: 'Next Day Parcel',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}
		if (ltl > 0) {
			shippingGroups.push({
				groupShipModeCode: 'LTL',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}

		// Shipping Grouping Revision
		// We're going to loop through shipping groups with a tracking number first, then ones that still don't have one.
		for (const item of this.trackingItems) {
			// get the placeholder image if one has not been returned for the item
			if (item.imageIdentifier === '') {
				item.imageIdentifier = 'IMAGE_NOT_FOUND';
			}
			// 6/6/2022: null values for estimated delivery were causing orders to be incorrectly displayed as cancelled.
			if (item.status !== 'Cancelled' && item.status !== 'In Process') {
				for (const shipGroup of shippingGroups) {
					if (item.trackingNumber === shipGroup.groupTrackingNumber) {
						shipGroup.orderStatus = this.orderDetail.status;
						shipGroup.orderTrackingNumber = this.orderDetail.trackingNumber;
						shipGroup.orderTrackingUrl = this.orderDetail.trackingUrl;
						shipGroup.groupStatus = item.status;
						if (item.estimatedDelivery !== null) {
							shipGroup.groupEstimatedDelivery = item.estimatedDelivery;
						} else {
							shipGroup.groupEstimatedDelivery = '';
						}
						shipGroup.groupTrackingUrl = item.trackingUrl;
						shipGroup.groupTrackingNumber = item.trackingNumber;
						shipGroup.groupStartEstimatedDelivery = item?.startEstimatedDelivery || '';
						shipGroup.groupShipModeCode = item.shipModeCode;
						shipGroup.groupDeliveryMethod = item.deliveryMethod;
						shipGroup.groupShippingAddress = item.shippingRecipient?.address?.address1 || '';
						shipGroup.groupShippingCity = item.shippingRecipient?.address?.city || '';
						shipGroup.groupShippingState = item.shippingRecipient?.address?.state || '';
						shipGroup.groupTrackingStatus = item.trackingStatus || 'Please check tracking link for more details';
						shipGroup.groupTrackingLocation = item.trackingLocation || 'Please check tracking link for more details';
						shipGroup.items.push(item);

						if (shipGroup.orderStatus === 'Shipped' && shipGroup.groupStatus === 'Completed' && shipGroup?.groupTrackingLocation.trim() === ',') {
							shipGroup.groupStatusCode = 'In Process';
						} else if (shipGroup?.groupTrackingLocation.trim() !== ',' && (shipGroup.orderStatus === 'Completed' || shipGroup.orderStatus === 'Fulfilled' || shipGroup.orderStatus === 'In Fulfillment') && shipGroup.groupTrackingStatus !== 'Delivered') {
							shipGroup.groupStatusCode = 'Shipped';
						} else if (shipGroup.groupTrackingStatus === 'Delivered') {
							shipGroup.groupStatusCode = 'Delivered';
							this.orderCompleted = true;
						}
					}
				}
			}
		}

		for (const item of this.noTrackItems) {
			// this fixed an issue where item(s) without tracking were also being injected into shipping groups
			// that had tracking numbers.
			const shipGroupsNoTrack: ShippingGroup[] = [];
			shippingGroups.forEach((group) => {
				if (group.groupTrackingNumber === 'NO_TRACKING_AVAILABLE_YET') {
					shipGroupsNoTrack.push(group);
				}
			});

			for (const shipGroup of shipGroupsNoTrack) {
				// make sure items go into the correct ship mode bucket
				if (item.trackingNumber === '' && item.shipModeCode.substring(0,3).toLowerCase() === shipGroup.groupShipModeCode.substring(0,3).toLowerCase() && item.status !== 'Cancelled') {
					shipGroup.orderStatus = this.orderDetail.status;
					shipGroup.orderTrackingNumber = this.orderDetail.trackingNumber;
					shipGroup.orderTrackingUrl = this.orderDetail.trackingUrl;
					if (item.estimatedDelivery !== null) {
						shipGroup.groupEstimatedDelivery = item.estimatedDelivery;
					} else {
						shipGroup.groupEstimatedDelivery = '';
					}
					shipGroup.groupTrackingUrl = item?.trackingUrl || '';
					shipGroup.groupStartEstimatedDelivery = item?.startEstimatedDelivery || '';
					shipGroup.groupShipModeCode = item.shipModeCode;
					shipGroup.groupDeliveryMethod = item.deliveryMethod;
					shipGroup.groupShippingAddress = item.shippingRecipient?.address?.address1 || '';
					shipGroup.groupShippingCity = item.shippingRecipient?.address?.city || '';
					shipGroup.groupShippingState = item.shippingRecipient?.address?.state || '';
					shipGroup.groupTrackingStatus = item.trackingStatus;
					shipGroup.groupTrackingLocation = item.trackingLocation;
					shipGroup.items.push(item);
					if (shipGroup.orderStatus === 'Order Placed' && shipGroup.orderTrackingNumber === null) {
						shipGroup.groupStatusCode = 'Order Placed';
					}
				}
			}
		}
		return shippingGroups.sort((a, b) => (a.items[0].estimatedDelivery > b.items[0].estimatedDelivery) ? 1 : -1 );
	}

	sortBopisItems(items) {
		// 1. get reduced pickup stores (sort bopis bucket by store)
		// 2. create temp active bopis group and temp cancelled bopis group
		// 3. sort by store and then by status
		// 4. push to appropriate status group
		const stores = this.reduceValues(items, 'store');
		for (const store of stores) {
			const tempActiveBopisItems: SortedBopisItemGroup = {
				store,
				items: [],
				status: '',
			};
			const tempCancelledBopisItems: SortedBopisItemGroup = {
				store,
				items: [],
				status: '',
			};
			const tempReturnedBopisItems: SortedBopisItemGroup = {
				store,
				items: [],
				status: '',
			};
			for (const item of items) {
				if (item.imageIdentifier === '') {
					item.imageIdentifier = 'IMAGE_NOT_FOUND';
				}
				if (item.store === store && item.status !== 'Cancelled' && item.status !== 'Returned') {
					tempActiveBopisItems.store = this.storeNameReformat(item.store);
					tempActiveBopisItems.status = item.status;
					tempActiveBopisItems.estimatedDeliveryDate = item.estimatedDelivery;
					tempActiveBopisItems.startEstimatedDeliveryDate = item.startEstimatedDelivery;
					tempActiveBopisItems.locationChain = this.getBopisGroupChain(item?.shippingRecipient?.address?.address1); // e.g. 'dsg'
					// check for null or empty shippingRecipient
					if (item.deliveryMethod.toLowerCase() !== 'bopis' && item.deliveryMethod.toLowerCase() !== 'bopl' && item.shippingRecipient?.address?.address1 !== '' && item.shippingRecipient?.address?.address1 !== null) {
						tempActiveBopisItems.store = this.toTitleCase(item?.shippingRecipient?.address?.address2);
						tempActiveBopisItems.locationAddress = this.formatBopisAddress(
							item?.shippingRecipient?.address?.address3,
							item?.shippingRecipient?.address?.city,
							item?.shippingRecipient?.address?.state,
							item?.shippingRecipient?.address?.postalCode
						);
					} else {
						tempActiveBopisItems.locationAddress = `USE_NO_ADDRESS`;
					}
					tempActiveBopisItems.items.push(item);
					if (item.status === 'Picked Up') {
						this.orderCompleted = true;
					}
				} else if (item.store === store && item.status === 'Cancelled') {
					tempCancelledBopisItems.status = 'Cancelled';
					tempCancelledBopisItems.items.push(item);
				} else if (item.store === store && item.status === 'Returned') {
					tempReturnedBopisItems.status = 'Returned';
					tempReturnedBopisItems.items.push(item);
				}
			}
			this.sortedActiveBopisItems.push(tempActiveBopisItems);
			this.sortedCancelledBopisItems.push(tempCancelledBopisItems);
			this.sortedReturnedBopisItems.push(tempReturnedBopisItems);
		}
	}

	/**
	 * removes duplicates of the entered key
	 */
	reduceValues(items: OrderItem[], key: string): string[] {
		const temp = [];
		for (const item of items) {
			if (item[key]) {
				temp.push(item[key]);
			}
		}
		return [...new Set(temp)];
	}

	// for Same Day orders
	getCarrierShipmentIds(items) {
		const temp = [];
		for (const item of items) {
			for (let i = 0; i < item.orderTracking[0].packages.length; i++) {
				temp.push(item.orderTracking[0].packages[i].carrierShipmentId);
			}
		}
		return [...new Set(temp)];
	}

	/**
	 * if the order has a proxy pickup person, display this information
	 * @param bopisItems The sorted bopis items
	 * @returns BopisProxyPerson
	 */
	getBopisProxyIfPresent(bopisItems): BopisProxyPerson {
		if (bopisItems[0]?.proxyDetails.firstName) {
			return {
				firstName: bopisItems[0].proxyDetails.firstName,
				lastName: bopisItems[0].proxyDetails.lastName,
				email: bopisItems[0].proxyDetails.email
			};
		} else if (bopisItems[0]?.proxyDetails.length === 0) {
			return {
				firstName: bopisItems[0].proxyDetails.firstName,
				lastName: bopisItems[0].proxyDetails.lastName,
				email: bopisItems[0].proxyDetails.email
			};
		}
		// no bopis proxy attached the the order
		return {firstName: null, lastName: null, email: null};
	}

	sortShipItemsByEDD(items: OrderItem[]): OrderItem[] {
		return items.sort((a, b) => a.estimatedDelivery.localeCompare(b.estimatedDelivery));
	}

	sortBopisItemsByPickupLocation(items: OrderItem[]): OrderItem[] {
		return items.sort((a, b) => a.store.localeCompare(b.store));
	}




	/**
	 * Reformat Store Name from Item data. Content is passed as all-caps
	 * @param storeName the item.store value on a bopis order item. e.g. 'DICKS SPORTING GOODS - ROBINSON TOWN CENTRE'
	 * @returns a reformatted string value
	 */
	storeNameReformat(storeName: string): string {
		const regDSG = /^D/ig;
		const regGG = /^GOL/ig;
		const regG3 = /^GOI/ig;
		const regPL = /^P/ig;
		const orderChain = storeName.split('-')[0].trim(); // e.g. 'DICKS SPORTING GOODS'
		const storeLocation = storeName.split('-')[1].trim(); // e.g. 'ROBINSON TOWN CENTRE'
		if (regDSG.test(orderChain)) {
			return `DICK'S Sporting Goods - ${storeLocation}`;
		} else if (regGG.test(orderChain)) {
			return `Golf Galaxy - ${storeLocation}`;
		} else if (regG3.test(orderChain)) {
			return `Going Going Gone - ${storeLocation}`;
		} else if (regPL.test(orderChain)) {
			return `Public Lands - ${storeLocation}`;
		} else {
			// something unexpected comes through? Just return the unaltered store value
			return storeName;
		}
	}

	/**
	 * Determine the chain for a BOPIS/BOPL item
	 * @param storeName
	 * @returns chain abbreviation as string
	 */
	getBopisGroupChain(storeName: string): string {
		const regDSG = /^D/ig;
		const regGG = /^GOL/ig;
		const regG3 = /^GOI/ig;
		const regPL = /^P/ig;
		const orderChain = storeName.split('-')[0].trim(); // e.g. 'DICKS SPORTING GOODS'

		if (regDSG.test(orderChain)) {
			return 'dsg';
		} else if (regGG.test(orderChain)) {
			return 'gg';
		} else if (regG3.test(orderChain)) {
			return 'ggg';
		} else if (regPL.test(orderChain)) {
			return 'pl';
		}
	}

	/**
	 * Determine and reformat street address for BOPIS/BOPL location
	 * @param addr
	 * @param city
	 * @param state
	 * @param zip
	 * @returns single string concatenated with address converted to title case
	 */
	formatBopisAddress(addr: string, city: string, state: string, zip: string): string {
		return `${this.toTitleCase(addr)} ${this.toTitleCase(city)}, ${state} ${zip}`;
	}

	openTaxDisclaimerModal() {
		this.dialog.open(TaxDialogComponent, {
			width: '600px'
		});
	}

	openDiscountModal(adjustment: Adjustment) {
		const dialogRef = this.dialog.open(AdjustmentDialogComponent, {
			width: '600px'
		});
		dialogRef.componentInstance.htmlContent = adjustment.longDescription;
	}

	getManualAdjustmentAmount(adjustment: Adjustment): number {
		return Math.abs(adjustment.amount);
	}

	openManualDiscountModal(adjustment: Adjustment) {
		const dialogRef = this.dialog.open(ManualAdjustmentDialogComponent, {
			width: '600px'
		});

		dialogRef.componentInstance.amount = this.getManualAdjustmentAmount(adjustment);
	}

	getOrderItemAdjustments(orderItem: OrderItem) {
		return orderItem.adjustments.filter((x) => x.displayLevel === 'OrderItem');
	}

	analyticsClick(eventType: string, eventName: string): void {
		this.analyticsService.setEventType(eventType);
		this.analyticsService.triggerAnalyticsEvent(eventName);
	}

	getLink(orderItem: OrderItem) {
		return orderItem.pdpPageUrl;
	}

	getPdpPageUrl(storeId: number, orderItem: OrderItem): string {
		if (!orderItem.pdpSeoUrl) {
			return;
		}
		let pdpPageUrl: string;

		/*
		 * store matching through storeId because
		 * users are able to see cross-chain products
		 * in their order details
		 */
		switch(storeId) {
			case 15108:
				pdpPageUrl = `https://www.dickssportinggoods.com${orderItem.pdpSeoUrl}?sku=${orderItem.sku}`;
				break;
			case 10701:
				pdpPageUrl = `https://www.golfgalaxy.com${orderItem.pdpSeoUrl}?sku=${orderItem.sku}`;
				break;
			case 16066:
				pdpPageUrl = `https://www.publiclands.com${orderItem.pdpSeoUrl}?sku=${orderItem.sku}`;
				break;
			default:
				break;
		}
		return pdpPageUrl;
	}

	setPdpUrls(items: OrderItem[]) {
		items.forEach(item =>
			item.pdpPageUrl = this.getPdpPageUrl(this.orderDetail.storeUniqueId, item)
		);
	}

	toTitleCase(str: string): string {
		return str.split(' ').map(w => w[0].toUpperCase() + w.substring(1).toLowerCase()).join(' ');
	}
	// setters for zip/adjustments/payments - tracking a different order was not updating these
	setZipCode(zip?: string) {
		this.zipCode = zip;
	}

	setOrderAdjustments(adjustments) {
		this.orderAdjustments = adjustments;
	}

	setManualAdjustments(manualAdjustments) {
		this.orderManualAdjustments = manualAdjustments;
	}

	/**
	 * reset all buckets to empty for displaying an additional order to track
	 */
	resetOrderTrack(): void {
		this.bopisItemsBucket = [];
		this.sthItemsBucket = [];
		this.sameDayItemsBucket = [];
		this.tempSortedShippingOrder = [];
		this.tempSortedBopisItems = [];
		this.sortedBopisItems = [];
		this.sortedActiveBopisItems = [];
		this.sortedCancelledBopisItems = [];
		this.sortedReturnedBopisItems = [];
		this.sortedShippingItems = [];
		this.sortedSameDayItems = [];
		this.orderProxyPerson = {firstName: '', lastName: '', email: ''};
		this.sortedShippingGroups = [];
		this.sortedSameDayGroups = [];
		this.returnedOrCancelledShippingItems = { cancelled: [], returned: []};
		this.returnedOrCancelledSameDayDeliveryItems = { cancelled: [], returned: []};
		this.orderCompleted = false;
		this.orderAdjustments = [];
		this.orderManualAdjustments = [];
		this.noTrackItems = [];
		this.sddNoTracking = [];
		this.trackingItems = [];
		this.sddHasTracking = [];
	}

	findProxyInfo(items: OrderItem[]){
		for(let i = 0; i < items.length; i++){
			if(items[i].proxyDetails?.firstName){
				this.proxyPickup = items[i].proxyDetails;
				i = items.length;
			}
		}
	}

	redirectToPDP(redirectUrl: string, sku: string) {
		this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {event: 'BuyAgain_clicked', value: sku});
		this.window.location.href = `${redirectUrl}&recid=buyItAgain_OrderHistory`;
	}


	openReviewModal(productId: string) {
		this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', { event : 'OrderHistoryWriteAReviewClick', value : true});
		const campaignId = 'MyAccount';
		if (this.reviewsSubmissionFormV2Enabled) {
			this.bvFormService.openReviewSubmissionV2(
				productId,
				campaignId
			);
		} else {
			$BV.ui('rr', 'submit_review', {
				productId,
				campaignId
			});
		}
    }

	handleImageError(imgEl: any, requestedImg: string): void {
		imgEl.src = DCSG_CONSTANTS.imageUnavailable;
		this.analyticsService.emitMissingImageEvent({
			pageUrl: window.location.href,
			requestedImg: requestedImg
		});
	}

	private getKillswitches() {
		this.store.selectDistinctKey('ENVIRONMENT_KILLSWITCH_VALUES').pipe(first()).subscribe((killswitch: KillSwitches) => {
			this.buyAgainEnabled = this.utilityService.checkKillSwitch('buyAgainEnabled', killswitch, this.chain.chainIdentifierAbbr);
			this.reviewsSubmissionFormV2Enabled = this.utilityService.checkKillSwitch('reviewsSubmissionFormV2Enabled', killswitch, this.chain.chainIdentifierAbbr);
		});
	}

	private getContactInfoForInStoreOrder(storeInfo: StoreInfo){
		const inStore = storeInfo != null;
		if(inStore){
			this.store.selectDistinctKey<PersonalInformation>('PERSONAL_INFO', true).subscribe((info) => {
				const phoneNumber = info.phoneNumbers.length > 0 ? info.phoneNumbers[0]?.phoneNumber : '';
				this.inStoreOrder = inStore;
				this.identity = {
					firstName: info.firstName,
					lastName: info.lastName,
					email: info.email,
					phone: phoneNumber,
				};
			});
		}
	}

	openModal(){
		this.confirmationEmail?.reloadConfirmation();
		this.resendConfirmationModal?.openModal();
	}

	closeModal(){


		this.resendConfirmationModal?.closeModal();

	}
}
